<template>
<div class="chapter">
<div class="inner_page">
<h1 class="chapter-title"><a href="#/toc#toc-chapter001">I</a></h1>
<p class="indent"><a id="page3"></a>In the morning he sings in the john. You can imagine what an effervescent and physically-fit person this is. The desire to
         sing arises in him like a reflex. These songs of his, in which there’s neither melody nor words but just a “ta-ra-ra,” which
         he pipes out in permutations, can be interpreted like this:
      </p>
<p class="indent">“How I like to live… ta-ra! ta-ra!.. My bowels are buoyant… ra-ta-ta-ta-ra-ree… In me the fluids flow flawlessly … ra-ta-ta-doo-ta-ta…
         Gush, guts, gush… tram-ba-ba-boom!”
      </p>
<p class="indent">When he passes by me in the morning on his way from the bedroom (I pretend to be asleep) to the door leading into the viscera
         of the apartment, the bathroom, my imagination follows him. I hear the commotion in the phone booth of a bathroom, where it’s
         tight for his bulky body. His back bangs against the inner side of the closed door and his elbows hit the walls; he shuffles
         his feet. Inset in the bathroom door is frosted, oval glass. He turns the switch; the oval is illuminated from within and
         becomes a beautiful opal-colored egg. With a mental stare I see this egg hanging in the darkness of the corridor.
      </p>
<p class="indent">He weighs about 220 pounds. Not long ago, when walking down stairs somewhere, he noticed how his breasts bounced in beat with
         his feet. Therefore he decided to add a new series of gymnastic exercises.
      </p>
<p class="indent">This is a model, masculine specimen.</p>
<p class="indent">Usually he indulges in gymnastics, not in his own bedroom, <a id="page4"></a>but in that room of unprescribed purpose where I am kept. Here it’s roomier, airier; there’s more light, more radiance. In
         through the open balcony door pours coolness. Besides this, here there’s a sink. The mat is moved in from the bedroom. He’s
         stripped to the waist, wearing knit longies fastened by a single button in the middle of his belly. The azure and rose-colored
         world of the room revolves in the mother-of-pearl objective of the button. When he lays his back on the mat and begins to
         raise his legs in turn, the button can’t bear it. The groin is unveiled. His groin is grand. The tender spot. The forbidden
         corner. The groin of a production man. It’s the same such groin of suede dullness I saw on a buck antelope. The girls, his
         secretaries and clerks, would certainly be penetrated by love currents from just one glimpse of it.
      </p>
<p class="indent">He washes like a little boy: pipes, hops, snorts, emits howls. He captures water by handfuls and not quite getting it to his
         armpits, splashes the mat. The water scatters on the straw in full, clean drops. The foam falling into the basin bubbles like
         a pancake. Sometimes the soap blinds him–swearing, he tears at his eyelids with his thumbs. He rinses his throat with a screech.
         Under the balcony people stop and throw back their heads.
      </p>
<p class="indent">The rosiest, quietest morning. Spring in full swing. On all the window sills stand flower boxes. Through their slits seeps
         the cinnabar of a forthcoming florescence.
      </p>
<p class="indent">(Things don’t like me. Furniture tries to trip me. Once some sort of lacquered corner literally bit me. With my blanket I
         always have complex interrelations. Soup which is served me never cools. If some kind of trinket–a coin or a cuff link–falls
         off the table, it usually rolls under some piece of almost immovable furniture. I crawl along the floor and, raising my head,
         I see how the sideboard is laughing.)
      </p>
<p class="indent">The blue straps of his suspenders hang at his sides. He goes to the bedroom, finds his pince-nez on the table, puts it on
         in front of the mirror and returns to my room. Here, standing in the center, he raises the straps of his suspenders, both
         at once, with such a movement as if hoisting a load onto his shoulders. He doesn’t say a word to me. I pretend to be asleep.
         In the metallic plates of his suspenders the sun concentrates in two burning bundles. (Things like him.)
      </p>
<p class="indent"><a id="page5"></a>He doesn’t have to comb his hair or groom his beard and mustache. His hair is cut short; his mustache is short–just under
         the nose. He resembles a grown-up fat boy.
      </p>
<p class="indent">He took a bottle; the glass stopper squeaked. He poured Eau du Cologne into his palm and passed the palm along the sphere
         of his head from his forehead to the nape of his neck and back again.
      </p>
<p class="indent">In the morning he drinks two glasses of cold milk: he gets the pitcher from the sideboard, pours and drinks without sitting
         down.
      </p>
<p class="indent">My first impression of him stunned me. I can’t have assumed or supposed it. Smelling of Eau de Cologne, he stood before me
         in an elegant gray suit. His lips were fresh, slightly protruded. It turned out he’s a dude.
      </p>
<p class="indent">At night I’m often awakened by his snoring. In a daze, I don’t understand what’s the matter. It’s as if someone utters threateningly
         over and over: “Krakatou… Krra… ka… touuu…”
      </p>
<p class="indent">They’ve given him a magnificent apartment. You should see the vase that stands on a lacquered pedestal near the balcony door.
         A vase of the thinnest china, round, tall, transilluminated with a delicate blood-vessel redness. It resembles a flamingo.
         The apartment is on the third floor. The balcony hangs out in the open. The wide suburban street looks like a highway. Across
         the street below there’s a garden: a thick tree-ridden garden, typical of Moscow suburbs, an unarranged assemblage which grew
         up on an empty lot walled-in on three sides, like an oven.
      </p>
<p class="indent">He’s a glutton. He has dinner out. Last night he came home hungry and decided to have a snack. There was nothing in the sideboard.
         He went downstairs (there’s a store on the corner) and hauled back a whole bunch of stuff: half a pound of ham, a can of sprats,
         canned mackerel, a large loaf of bread, a good-sized half-moon of Dutch cheese, four apples, ten eggs and “Persian Pea” candied
         fruits. Fried eggs and tea were ordered (there’s a communal kitchen in the house where two cooks take turns serving).
      </p>
<p class="indent">“Dig in, Kavalerov,” he invited me and bore down himself. He ate the fried eggs right from the pan and removed the bits of
         shell as if he were chipping off enamel. His eyes became bloodshot; he took off and put on his pince-nez, smacked his lips,
         snorted; and his ears moved.
      </p>
<p class="indent"><a id="page6"></a>I amuse myself with observations. Have you ever noticed how salt falls off the end of a knife without leaving a trace–the
         knife shines as if it were never touched; that a pince-nez runs over the bridge of a nose like a bicycle; that a person is
         surrounded by little inscriptions, a sprawling anthill of small inscriptions: on forks, spoons, plates, the rim of a pince-nez,
         buttons, pencils? No one notices them. They struggle for existence. They pass from one form to another, up to immense sign-board
         letters! They rise up–class against class: the letters of street signs wage war with the letters of playbills.
      </p>
<p class="indent">He ate his fill. He reached for the apples with a knife but only slit the yellow cheek of an apple and discarded it.</p>
<p class="indent">One people’s commissar praised him highly in a speech:</p>
<p class="indent">“Andrei Babichev is one of the state’s most remarkable people.”</p>
<p class="indent">He, Andrei Petrovich Babichev, occupies the post of director of the food industry trust. He’s a mighty sausage maker, confectioner,
         and cook.
      </p>
<p class="indent">And I, Nikolai Kavalerov, am a jester in his presence.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// chapter001.vue?5efeb704